
import React from 'react';
import { motion } from 'framer-motion';

const shimmerVariants = {
  hidden: {
    backgroundPosition: '-1000px 0',
  },
  show: {
    backgroundPosition: '1000px 0',
    transition: {
      repeat: Infinity,
      duration: 2.5,
      ease: "linear",
    },
  },
};

const SkeletonBase = ({ className = '', rounded = 'rounded-xl' }) => {
  return (
    <div className={`relative overflow-hidden bg-white/50 backdrop-blur-sm border border-white/40 ${rounded} ${className}`}>
      {/* Shimmer Effect */}
      <motion.div
        variants={shimmerVariants}
        initial="hidden"
        animate="show"
        className="absolute inset-0 z-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
        style={{
          backgroundSize: '1000px 100%',
        }}
      />
    </div>
  );
};

export default function LoadingSkeleton({ variant = 'card', className = '' }) {
  if (variant === 'chart') {
    return (
      <div className={`w-full relative ${className}`}>
        {/* Chart Area */}
        <SkeletonBase className="w-full h-full rounded-2xl shadow-sm border border-primary-50/50" />

        {/* Fake Axes Lines (Subtle) */}
        <div className="absolute inset-0 p-6 flex flex-col justify-between opacity-30 pointer-events-none">
          <div className="w-full h-px bg-gray-200/50"></div>
          <div className="w-full h-px bg-gray-200/50"></div>
          <div className="w-full h-px bg-gray-200/50"></div>
          <div className="w-full h-px bg-gray-200/50"></div>
        </div>
      </div>
    );
  }

  if (variant === 'list') {
    return (
      <div className={`space-y-4 ${className}`}>
        {[1, 2, 3].map((n) => (
          <div key={n} className="flex items-center p-4 bg-white/60 backdrop-blur-xl border border-white/50 rounded-2xl shadow-sm">
            <SkeletonBase className="w-12 h-12 rounded-xl mr-4 bg-gray-100/50" />
            <div className="flex-1 space-y-2">
              <SkeletonBase className="h-4 w-1/3 bg-gray-100/50 rounded-lg" />
              <SkeletonBase className="h-3 w-1/2 bg-gray-100/50 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Default 'card' variant (mimics the Scanner Card or similar)
  // Can be used for generic blocks if className overrides height/width
  return (
    <div className={`p-6 bg-white/60 backdrop-blur-xl border border-white/50 shadow-lg rounded-2xl relative overflow-hidden group ${className}`}>
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
        {/* Icon Placeholder */}
        <SkeletonBase className="w-14 h-14 rounded-2xl bg-gray-50/80 shrink-0" />

        <div className="flex-1 w-full space-y-3">
          {/* Title Line */}
          <SkeletonBase className="h-5 w-3/4 md:w-1/3 bg-gray-100/50 rounded-lg" />
          {/* Description Lines */}
          <div className="space-y-2">
            <SkeletonBase className="h-3 w-full md:w-2/3 bg-gray-50/50 rounded-lg" />
            <SkeletonBase className="h-3 w-5/6 md:w-1/2 bg-gray-50/50 rounded-lg" />
          </div>
        </div>
      </div>

      {/* Bottom Actions Row (Optional visual flair) */}
      <div className="mt-6 flex gap-3 opacity-60">
        <SkeletonBase className="h-8 w-20 rounded-lg bg-gray-50/30" />
        <SkeletonBase className="h-8 w-20 rounded-lg bg-gray-50/30" />
      </div>
    </div>
  );
}
